import React, { useState, useEffect } from 'react';
import { Box, BoxProps, CircularProgress } from '@mui/material';

interface SvgLoaderProps extends BoxProps {
  url: string;
  alt?: string;
}

/**
 * Component that fetches an SVG from a URL and injects it directly into the DOM.
 * This allows for styling and manipulation of SVG elements that wouldn't be possible
 * with a standard img tag.
 */
export function SvgLoader({
  url,
  alt,
  width = '24px',
  height = '24px',
  ...boxProps
}: SvgLoaderProps) {
  const [loading, setLoading] = useState(true);
  const [svgContent, setSvgContent] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSvg = async () => {
      if (!url) {
        setError('No URL provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch SVG: ${response.status} ${response.statusText}`
          );
        }

        const svgText = await response.text();
        if (!svgText.includes('<svg')) {
          throw new Error('Invalid SVG content');
        }

        setSvgContent(svgText);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load SVG');
        console.error('Error loading SVG:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSvg();
  }, [url]);

  if (loading) {
    return (
      <Box
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        width={width}
        height={height}
        {...boxProps}
      >
        <CircularProgress
          size={Math.min(
            typeof width === 'number' ? width / 2 : 12,
            typeof height === 'number' ? height / 2 : 12
          )}
        />
      </Box>
    );
  }

  if (error || !svgContent) {
    return (
      <Box
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        width={width}
        height={height}
        title={error || 'Failed to load SVG'}
        role="img"
        aria-label={alt || 'Failed SVG'}
        {...boxProps}
      >
        ⚠️
      </Box>
    );
  }

  return (
    <Box
      width={width}
      height={height}
      display="inline-flex"
      dangerouslySetInnerHTML={{ __html: svgContent }}
      role="img"
      aria-label={alt || 'SVG Image'}
      {...boxProps}
    />
  );
}
